import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type { ApiResponse, BusinessRecordDetailsDto, OrgDetailsDto, UpdateOrgRequestDto } from './organisationApiTypes';

const base = () => `${getApiBaseUrl()}/api/organisation`;

const parseOrThrow = async <T>(response: Response): Promise<ApiResponse<T>> => {
  if (!response.ok) {
    const failure = await response.json().catch(() => undefined) as { message?: string } | undefined;
    throw new Error(failure?.message || `Request failed: ${response.status}`);
  }
  return response.json() as Promise<ApiResponse<T>>;
};

export const fetchOrganisations = async (): Promise<ApiResponse<OrgDetailsDto[]>> => {
  const res = await authFetch(base());
  return parseOrThrow<OrgDetailsDto[]>(res);
};

export const fetchOrganisationById = async (organisationId: string): Promise<ApiResponse<OrgDetailsDto>> => {
  const res = await authFetch(`${base()}/${organisationId}`);
  return parseOrThrow<OrgDetailsDto>(res);
};

export const fetchBusinessRecords = async (organisationId: string): Promise<ApiResponse<BusinessRecordDetailsDto[]>> => {
  const res = await authFetch(`${getApiBaseUrl()}/api/organisations/${organisationId}/business-records`);
  return parseOrThrow<BusinessRecordDetailsDto[]>(res);
};

export const createOrganisation = async (request: Omit<UpdateOrgRequestDto, 'id'> & { regionCode: string; defaultCurrency: string; defaultTimezone: string; financialYearEndBasis: string }): Promise<ApiResponse<OrgDetailsDto>> => {
  const res = await authFetch(base(), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) });
  return parseOrThrow<OrgDetailsDto>(res);
};

export const createBusinessRecord = async (organisationId: string, request: { legalName: string; tradingName?: string; abn?: string; abnBranch?: string; status?: string }): Promise<ApiResponse<BusinessRecordDetailsDto>> => {
  const res = await authFetch(`${getApiBaseUrl()}/api/organisations/${organisationId}/business-records`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request) });
  return parseOrThrow<BusinessRecordDetailsDto>(res);
};

export const updateOrganisation = async (request: UpdateOrgRequestDto): Promise<ApiResponse<OrgDetailsDto>> => {
  const res = await authFetch(base(), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return parseOrThrow<OrgDetailsDto>(res);
};
